import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, CheckCircle2, AlertCircle, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * GoogleAuthCallback
 * Detects `#session_id=...` in the URL hash after returning from Emergent OAuth,
 * exchanges it for a JWT and clears the hash. Renders an in-page toast.
 */
export const GoogleAuthCallback = () => {
  const { exchangeGoogleSession } = useAuth();
  const { t } = useLanguage();
  const a = t.auth || {};
  const hasProcessed = useRef(false);
  const [state, setState] = useState("idle"); // idle | exchanging | success | error
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    if (!hash || !hash.includes("session_id=")) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(hash.slice(1));
    const sid = params.get("session_id");
    if (!sid) return;

    setState("exchanging");
    (async () => {
      try {
        await exchangeGoogleSession(sid);
        setState("success");
        // Clean URL hash + query
        try {
          const u = new URL(window.location.href);
          u.hash = "";
          u.searchParams.delete("google");
          window.history.replaceState({}, "", u.toString());
        } catch {}
        setTimeout(() => setState("idle"), 2500);
      } catch (e) {
        setError(e.message);
        setState("error");
        try {
          const u = new URL(window.location.href);
          u.hash = "";
          u.searchParams.delete("google");
          window.history.replaceState({}, "", u.toString());
        } catch {}
      }
    })();
  }, [exchangeGoogleSession]);

  if (state === "idle") return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-5 left-1/2 z-[120] -translate-x-1/2"
        data-testid={`google-auth-toast-${state}`}
      >
        <div
          className={`flex items-center gap-3 rounded-2xl border px-5 py-3 backdrop-blur-xl shadow-2xl ${
            state === "success"
              ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200"
              : state === "exchanging"
              ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-100"
              : "border-red-400/40 bg-red-400/10 text-red-200"
          }`}
        >
          {state === "exchanging" && <Loader2 className="h-4 w-4 animate-spin" />}
          {state === "success" && <CheckCircle2 className="h-5 w-5" />}
          {state === "error" && <AlertCircle className="h-4 w-4" />}
          <span className="text-sm font-bold">
            {state === "exchanging" && (a.google_processing || "Verifying with Google…")}
            {state === "success" && (a.success_login || "Welcome!")}
            {state === "error" && (error || a.error_generic || "Error")}
          </span>
          <button
            onClick={() => setState("idle")}
            className="ml-2 opacity-60 hover:opacity-100"
            aria-label="dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GoogleAuthCallback;
